import React, { Fragment, useEffect, useState, useRef } from "react";
import DownloadImage from "./download-image";
import EditedImage from "./edited-image";
import Loader from "components/loader/loader";
import LocaleShotSelector from "./locale-shot-selector";
import UploadIcon from "./upload-icon";
import { uploadImage, deleteEditedShot, getDataTransferItems } from "../lib/imageData";
import { isValid, FILE_SIZE_LABEL } from "components/form-inputs/imageUploader/lib/fileHelper";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faTrash, faRedo } from "@fortawesome/free-solid-svg-icons";
import * as notify from "shared/notify";

const UploadImage = ({ sourcePage, ...props }) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [hasEditedImage, setHasEditedImage] = useState(!!props.shot.editedBlobKey);
    const [rotation, setRotation] = useState(0);
    const [tempRotation, setTempRotation] = useState(0);

    const loadTimeout = useRef(null);

    useEffect(() => {
        setHasEditedImage(!!props.shot.editedBlobKey);

        return () => {
            clearTimeout(loadTimeout);
        };
    }, [props.shot]);

    useEffect(() => {
        if (rotation !== 0) {
            setRotation(0);
        }
    }, [props.shot.editedBlobKey]);

    useEffect(() => {
        loadTimeout.current = setTimeout(async () => {
            if (tempRotation !== 0) {
                props.rotateShot(props.shot.key, tempRotation);
                setTempRotation(0);
            }
        }, 450);
    }, [tempRotation]);

    const rotateImage = () => {
        if (isUploading) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        clearTimeout(loadTimeout);
        setRotation(rotation < 270 ? rotation + 90 : 0);
        setTempRotation(tempRotation < 270 ? tempRotation + 90 : 0);
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        setIsDragActive(true);
    };

    const onDragOver = (e) => {
        e.preventDefault();
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragActive(false);
    };

    const onDrop = (e) => {
        e.preventDefault();

        const fileList = getDataTransferItems(e);
        const acceptedFiles = [];
        const file = fileList[0];

        if (isValid(file)) {
            acceptedFiles.push(file);
            uploadFile(file);
            setIsUploading(true);
        }

        setIsDragActive(false);
    };

    const uploadFile = async (file) => {
        if (file) {
            if (!isValid(file)) {
                return;
            }

            setIsUploading(true);
            const formData = new FormData();
            formData.append("file", file);
            formData.append("imageType", 2);
            const shot = await uploadImage(props.wipKey, props.taskKey, props.shot.key, sourcePage, formData, props.lookups).catch((err) => {
                notify.error(err);
            });

            setIsDragActive(false);
            setIsUploading(false);

            if (!shot) {
                return false;
            }

            props.updateShot(shot);
            setHasEditedImage(true);

            return true;
        }
    };

    const deleteEdited = async () => {
        const confirmImageDelete = await notify.confirm("Are you sure you want to delete this image?");

        if (!confirmImageDelete) {
            return;
        }

        if (props.hasPegholeData && props.shot.imageView === "Front") {
            const confirmDelete = await notify.confirm("Deleting this image will require updating peghole markers. Continue?");

            if (!confirmDelete) {
                return;
            }
        }

        deleteEditedShot(props.wipKey, props.taskKey, props.shot.key).then(() => {
            const shot = {
                ...props.shot,
                editedBlobKey: "",
                editedContentLength: 0,
                editedImageUris: null,
                editedMimeType: "",
                rotation: 0,
            };
            setHasEditedImage(false);

            props.updateShot(shot);
        });
    };

    const renderUploadDragDrop = () => {
        const loadingHtml = isUploading ? <Loader /> : null;
        const actionClass = isUploading ? " -upload-active" : isDragActive ? " -drag-active" : props.roUpload ? " -read-only" : "";

        return (
            <div
                className={`upload-box${actionClass}`}
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                {loadingHtml}
            </div>
        );
    };

    const skipButton = props.allowSkip ? (
        <button
            className="button"
            onClick={props.updateSkip}
            title="Skip Editing"
            disabled={props.roSkipEditing}
        >
            <FontAwesomeIcon icon={faBan} />
        </button>
    ) : null;
    const imageOrUpload = hasEditedImage ? (
        <EditedImage
            wipKey={props.wipKey}
            rotation={rotation}
            shot={props.shot}
            shipmentKey={props.shipmentKey}
            shipmentProductKey={props.shipmentProductKey}
            isIE={props.isIE}
        />
    ) : !props.isCompleted ? (
        renderUploadDragDrop()
    ) : null;
    const deleteButton = hasEditedImage ? (
        !props.isCompleted && (
            <button
                className="button"
                onClick={deleteEdited}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        )
    ) : !props.isCompleted ? (
        <>
            <UploadIcon
                id={props.shot.key}
                uploadFile={uploadFile}
                disabled={props.roUpload}
            />
            {skipButton}
        </>
    ) : null;

    const rotateButton = hasEditedImage && !props.isCompleted && (
        <div
            className="button"
            onClick={rotateImage}
            disabled={props.roUpload || props.isRotating}
        >
            <FontAwesomeIcon icon={faRedo} />
        </div>
    );

    const message = !hasEditedImage ? <em>File size must be less than {FILE_SIZE_LABEL}</em> : <span>&nbsp;</span>;

    return (
        <Fragment>
            <div className="edit-image">{imageOrUpload}</div>
            <div className="edit-control">
                <div className="message">{message}</div>
                {props.children}
                <div className="button-group -top">
                    {deleteButton}
                    {rotateButton}
                    {hasEditedImage && (
                        <DownloadImage
                            title="Download Edited Image"
                            wipKey={props.wipKey}
                            type="edited"
                            shotKey={props.shot.key}
                            mimeType={props.mimeType}
                            imageView={props.shot.imageView}
                            sourcePage={sourcePage}
                            isReadOnly={props.roDownload}
                        />
                    )}
                    <div className="align-right">
                        <LocaleShotSelector
                            relationships={props.shot.relationships}
                            photoWipKey={props.wipKey}
                            shot={props.shot}
                            readOnly={props.isCompleted}
                            disabled={props.roUpload}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default UploadImage;
